import { useMemo } from 'react';
import type { ErpModule } from '@/api/platform.api';
import {
  ALL_APPROVAL_MODULES,
  buildApprovalModuleSelectOptions,
  filterApprovalModulesForUser,
  type ApprovalModuleOption,
} from '@/modules/approvals/approvalModuleOptions';
import { usePermissions } from '@/hooks/usePermissions';
import { useAppSelector } from '@/hooks/useAppSelector';
import { getLauncherModules, isMasterOperator } from '@/utils/moduleAccess';

/** Approval module dropdown options scoped to the user's login/subscribed apps. */
export function useAccessibleApprovalModules(options?: { includeAll?: boolean }) {
  const includeAll = options?.includeAll ?? true;
  const enabledModules = useAppSelector((s) => s.auth.enabledModules);
  const accessibleModules = useAppSelector((s) => s.auth.accessibleModules);
  const permissions = useAppSelector((s) => s.auth.user?.permissions ?? []);
  const roleCode = useAppSelector((s) => s.auth.user?.roleCode);
  const user = useAppSelector((s) => s.auth.user);
  const { has } = usePermissions();

  const modules = useMemo(() => {
    if (isMasterOperator(user)) {
      return ALL_APPROVAL_MODULES;
    }
    const apps = getLauncherModules(enabledModules, permissions, accessibleModules, roleCode, has);
    const accessibleErpModules = new Set<ErpModule>(apps.map((entry) => entry.erpModule));
    return filterApprovalModulesForUser(accessibleErpModules);
  }, [user, enabledModules, permissions, accessibleModules, roleCode, has]);

  const selectOptions = useMemo(
    () => buildApprovalModuleSelectOptions(modules, includeAll),
    [modules, includeAll],
  );

  const defaultModule = useMemo(() => modules[0]?.value ?? '', [modules]);

  return {
    modules,
    selectOptions,
    defaultModule,
    hasMultiple: modules.length > 1,
  } satisfies {
    modules: ApprovalModuleOption[];
    selectOptions: ApprovalModuleOption[];
    defaultModule: string;
    hasMultiple: boolean;
  };
}
